'use client';

import { cartApi } from '@/slice/cartApi';
import React, { useEffect, useRef, useState } from 'react';
import classes from '@/components/header/header.module.scss';
import {
  convertLanguageCode,
  convertLanguageName,
} from '@/lib/convert-language-code';
import { useSession } from 'next-auth/react';
import { usePathname, useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/lib/store';
import { setConfirmClickState, setConfirmMessage } from '@/slice/confirm';
import { Languages } from '@/lib/dictionaries';

const languageNotice = {
  ko: '한국어 언어 변경 시,\n통화도 KRW로 변경됩니다.\n변경하시겠습니까?',
  en: 'When changing the language to English,\nthe currency will also change to USD.\nWould you like to proceed?',
  ja: '日本語に言語を変更する場合、\n通貨もJPYに変更されます。\n変更しますか？',
  id: 'Saat Anda mengubah bahasa ke Bahasa Indonesia,\n mata uang juga akan berubah menjadi IDR.\nApakah Anda ingin melanjutkan?',
  zh: '言切换为中文时，\n货币也将更改为美元 (USD)。\n您确定要更改吗？',
};

const LanguageSelect = ({ lang }: { lang: Languages }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { data: session, update } = useSession();
  const dispatch = useDispatch();

  const confirmRequest = useRef<boolean>(false);
  const newLanguageRef = useRef<string>('');

  const confirmClickState = useSelector(
    (state: RootState) => state.confirm.clickState,
  );
  const cartRedirectUrl = useSelector(
    (state: RootState) => state.shopCart.redirectUrl,
  );

  const [dropdownView, setDropdownView] = useState(false);

  const handleToggleDropdown = () => {
    setDropdownView(!dropdownView);
  };

  const changeLanguage = async (e: any) => {
    const newLanguage = e.target.innerHTML;
    newLanguageRef.current = convertLanguageCode(newLanguage);
    confirmRequest.current = true;
    dispatch(
      setConfirmMessage(languageNotice[newLanguageRef.current as Languages]),
    );
  };

  const replace = (newLanguageCode: string) => {
    if (pathname.endsWith('/pay') && !pathname.includes('my/pay')) {
      router.replace(
        cartRedirectUrl.replace(`/${lang}`, `/${newLanguageCode}`),
      );
      return;
    }
    router.replace(pathname.replace(`/${lang}`, `/${newLanguageCode}`), {
      scroll: false,
    });
  };

  const fetchChangeLanguage = async () => {
    const newLanguage = newLanguageRef.current;
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/api/users/language`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          language: convertLanguageName(newLanguage, true),
        }),
      },
    );

    if (res.ok) {
      await update({
        ...session,
        user: {
          ...session!.user,
          language: newLanguage,
        },
      }).then(updateSession => {
        dispatch(cartApi.util.invalidateTags(['Cart']));
        replace(updateSession!.user.language!);
      });
    }
  };

  useEffect(() => {
    if (confirmRequest.current) {
      if (confirmClickState !== '') {
        dispatch(setConfirmMessage(''));
        dispatch(setConfirmClickState(''));
        if (confirmClickState === 'confirm') {
          const newLanguage = newLanguageRef.current;
          document.cookie = `i18nlang=${newLanguage}; path=/;`;
          if (session === null) {
            replace(newLanguage);
            return;
          }
          fetchChangeLanguage();
        }
      }

      confirmRequest.current = false;
    }
  }, [confirmClickState]);

  return (
    <div
      className={`${classes.langSelectorBox} ${dropdownView ? classes.active : ''}`}
    >
      <button className={classes.langSetBtn} onClick={handleToggleDropdown}>
        {convertLanguageName(lang, true)}
      </button>
      <ul className={classes.langSelectList}>
        {['ko', 'en', 'ja', 'id', 'zh'].map(language => (
          <li key={`language-option-${language}`}>
            <a className={classes.langSelectBtn} onClick={changeLanguage}>
              {convertLanguageName(language, true)}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LanguageSelect;
